import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Drives a live "try again in M:SS" countdown from a server-provided wait
 * duration. `start(seconds)` anchors a local deadline and ticks once a second
 * until it elapses, then stops on its own so the gated action re-enables. The
 * deadline is anchored on the client, so it counts a relative duration and is
 * unaffected by clock skew between client and server.
 *
 * Returns:
 *   start(seconds)            – begin (or restart) the countdown
 *   startFromMinutes(minutes) – same, from the server's fractional-minutes unit
 *   reset()                   – stop and clear immediately
 *   isWaiting                 – true while seconds remain (use it to gate the action)
 *   label                     – "M:SS" string for display
 *   disabledStyle             – inline style that greys out and blocks a gated
 *                               control while waiting; undefined otherwise
 */
export function useRetryCountdown() {
  const [remaining, setRemaining] = useState(0); // whole seconds left
  const intervalRef = useRef(null);

  const reset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRemaining(0);
  }, []);

  const start = useCallback((seconds) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const total = Math.max(0, Math.ceil(seconds));
    if (total === 0) {
      setRemaining(0);
      return;
    }

    const deadline = Date.now() + total * 1000;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
  }, []);

  // The server reports lockouts as fractional minutes (tryAgainInMinutes);
  // keep the unit conversion here so call sites don't each repeat it.
  const startFromMinutes = useCallback(
    (minutes) => start(Math.ceil((minutes ?? 0) * 60)),
    [start]
  );

  // Stop the interval if the component unmounts mid-countdown.
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const minutes = Math.floor(remaining / 60);
  const seconds = String(remaining % 60).padStart(2, "0");

  const isWaiting = remaining > 0;

  return {
    start,
    startFromMinutes,
    reset,
    isWaiting,
    label: `${minutes}:${seconds}`,
    disabledStyle: isWaiting ? { opacity: 0.5, pointerEvents: "none" } : undefined,
  };
}
